import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { usd } from "@/lib/format";

function Bar({ value, max, color = "#002FA7" }) {
  const w = Math.min(100, (value / max) * 100);
  return (
    <div className="h-1.5 bg-zinc-100 w-full">
      <div className="h-full" style={{ width: `${w}%`, background: color }} />
    </div>
  );
}

export default function Carriers() {
  const [cards, setCards] = useState([]);
  const [spend, setSpend] = useState(null);

  useEffect(() => {
    api.get("/carriers/scorecard").then(r => setCards(r.data));
    api.get("/analytics/spend").then(r => setSpend(r.data));
  }, []);

  const maxShip = Math.max(...cards.map(c => c.shipments_count), 1);
  const maxLaneSpend = spend ? Math.max(...spend.by_lane.map(l => l.value), 1) : 1;

  return (
    <div className="space-y-6 animate-slide-up" data-testid="carriers-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Carrier Scorecard</h1>
        <p className="text-sm text-zinc-500 mt-1">Measure carriers on cost, reliability, rollover, invoice accuracy and response time.</p>
      </div>

      <div className="bg-white border border-zinc-200">
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left">
                {["Carrier", "Shipments", "On-Time %", "Rollover %", "Avg Delay", "Invoice Accuracy", "D&D Exposure", "Avg Response", "Reliability Index"].map(h => <th key={h} className="px-4 py-2.5 data-label">{h}</th>)}
              </tr>
            </thead>
            <tbody data-testid="carriers-tbody">
              {cards.map(c => (
                <tr key={c.carrier} className="border-b border-zinc-100 hover:bg-zinc-50/60">
                  <td className="px-4 py-3 font-semibold">{c.carrier}</td>
                  <td className="px-4 py-3 metric">
                    <div className="flex items-center gap-2">
                      <span className="w-10">{c.shipments_count}</span>
                      <div className="flex-1 max-w-[80px]"><Bar value={c.shipments_count} max={maxShip} /></div>
                    </div>
                  </td>
                  <td className="px-4 py-3 metric">
                    <span className={c.on_time_pct >= 85 ? "text-[#166534]" : c.on_time_pct >= 75 ? "text-[#B45309]" : "text-[#991B1B]"}>{c.on_time_pct}%</span>
                  </td>
                  <td className="px-4 py-3 metric">
                    <span className={c.rollover_rate_pct <= 2 ? "text-[#166534]" : c.rollover_rate_pct <= 4 ? "text-[#B45309]" : "text-[#991B1B]"}>{c.rollover_rate_pct}%</span>
                  </td>
                  <td className="px-4 py-3 metric">{c.avg_delay_days}d</td>
                  <td className="px-4 py-3 metric">{c.invoice_accuracy_pct}%</td>
                  <td className="px-4 py-3 metric text-[#991B1B]">{usd(c.detention_exposure_usd)}</td>
                  <td className="px-4 py-3 metric">{c.avg_response_hrs}h</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="metric font-semibold w-10">{c.reliability_index}</span>
                      <div className="flex-1 max-w-[100px]"><Bar value={c.reliability_index} max={100} color={c.reliability_index >= 80 ? '#16A34A' : c.reliability_index >= 70 ? '#F59E0B' : '#DC2626'} /></div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {spend && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-white border border-zinc-200 p-5">
            <h3 className="font-heading text-lg font-bold tracking-tight mb-4">Spend by Trade Lane</h3>
            <div className="space-y-3">
              {spend.by_lane.slice(0, 8).map(l => (
                <div key={l.label}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-zinc-700">{l.label}</span>
                    <span className="metric text-zinc-900">{usd(l.value)}</span>
                  </div>
                  <Bar value={l.value} max={maxLaneSpend} />
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white border border-zinc-200 p-5">
            <h3 className="font-heading text-lg font-bold tracking-tight mb-4">Spend by Carrier</h3>
            <div className="space-y-3">
              {spend.by_carrier.map(c => {
                const maxCarrier = Math.max(...spend.by_carrier.map(x => x.value), 1);
                return (
                  <div key={c.label}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-zinc-700">{c.label}</span>
                      <span className="metric text-zinc-900">{usd(c.value)}</span>
                    </div>
                    <Bar value={c.value} max={maxCarrier} color="#002FA7" />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
